import { useContext } from "react"
import { GetServerSideProps } from "next"
import { http } from "src/services/http"
import { ApiUserData } from "src/models/user/ApiUserData"
import Gifting from "src/components/userPage/Gifting"
import GiftingNotAuth from "src/components/userPage/Gifting/GiftingNotAuth"
import GiftingFirstVisitModal from "src/components/userPage/Gifting/GiftingFirstVisitModal"
import { UserPageLayout } from "src/components/userPage/layout"
import { globalStoreContext } from "src/store/GlobalStore"
import { fetchUserReceivingGiftConditions } from "src/models/user/fetchUserReceivingGiftConditions"
import useSWR from "swr"
import { useLocalStorage } from "react-use"
import { BarLoader } from "react-spinners"
import colors from "src/config/color"
import styles from "./index.module.scss"
import { useTieupToast } from "../../../../components/hooks/useTieupToast"

type Props = {
  user: ApiUserData
}

export const getServerSideProps: GetServerSideProps<Props> = async ({
  query,
  res,
}) => {
  const { display_name } = query
  /** display_nameがなかったら404ページにリダイレクトする */
  if (!display_name) {
    res?.writeHead(302, { Location: "/404" })
    res?.end()
  }

  const user = await http
    .get<ApiUserData>(`/v1/user/${display_name}`)
    .then((v) => v.data)
    .catch((error) => {
      // eslint-disable-next-line no-console
      console.error(error)
    })

  // ユーザーが退会済み(404)、または退会申請中の場合は404に飛ばす
  if (!user || user.will_resign_datetime !== null) {
    return { notFound: true }
  }

  return { props: { user } }
}

const UserGift: React.FC<Props> = ({ user }) => {
  const {
    globalStore: {
      user: { apiUserData, clientState },
    },
  } = useContext(globalStoreContext)
  const { addToastError } = useTieupToast()

  const [isFirstVisitToGifting, setIsFirstVisitToGifting] =
    useLocalStorage<boolean>("is_first_visit_to_gifting", true)

  const { data: userReceivingGiftConditions, error: getConditionError } =
    useSWR(
      `/v1/gift/available/${user.id}`,
      () => fetchUserReceivingGiftConditions(user.id),
      { revalidateOnMount: true, revalidateOnFocus: false },
    )

  if (getConditionError) {
    addToastError(
      "ギフトの受け取り条件の取得に失敗しました。暫く後でお試し下さい",
    )
  }

  // ローディング
  if (clientState === null || userReceivingGiftConditions === undefined) {
    return (
      <UserPageLayout user={user} currentContent="Gift">
        <div className={styles.loader}>
          <BarLoader color={colors.spectrum.tieupTheme} />
        </div>
      </UserPageLayout>
    )
  }

  // ギフトを送れない場合の画面
  if (!userReceivingGiftConditions.canReceive()) {
    return (
      <UserPageLayout user={user} currentContent="Gift">
        <div className={styles.canNotReceiveGift}>
          <h2 className={styles.canNotReceiveGift_title}>
            クリエイターの都合によりギフトを贈ることができません。
          </h2>
          <p className={styles.canNotReceiveGift_text}>
            今後、クリエイターがギフトを贈れるようにする場合があります。
            <br />
            ギフトを贈れるようになるまでお待ちください。
          </p>
        </div>
      </UserPageLayout>
    )
  }

  return (
    <>
      <UserPageLayout user={user} currentContent="Gift">
        {clientState === 2 && apiUserData !== null ? (
          <Gifting user={user} isMe={apiUserData.id === user.id} />
        ) : (
          <GiftingNotAuth user={user} />
        )}
      </UserPageLayout>

      {clientState !== null && isFirstVisitToGifting && (
        <GiftingFirstVisitModal
          onClose={() => setIsFirstVisitToGifting(false)}
        />
      )}
    </>
  )
}

export default UserGift
